import { useQuery } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { ADMIN_API_BASE_URL, Button, EndpointHint, Skeleton } from '../../core/index.js'
import { ShiftPreferencesSummary, describePeriod } from '../../schedule/index.js'
import { listDrafts, listShiftPreferences, type PartnerSession } from '../lib/partnerApi.js'
import { ErrorNote, describeError } from './formControls.js'

/**
 * Plays a scheduler reading what each worker asked for before it builds
 * the month: `GET /facilities/{facilityId}/shift-preferences` as the partner
 * (`events:write` or `rankings:read`). Reading is all a partner may do
 * here; the worker sets them in their own app.
 */
export function ShiftPreferencesView({ session, facilityId, workers }: { session: PartnerSession; facilityId: string; workers: readonly { workerId: string; name: string }[] }) {
  const query = useQuery({
    queryKey: ['partner-simulator', 'shift-preferences', session.clientId, facilityId],
    queryFn: () => listShiftPreferences(ADMIN_API_BASE_URL ?? '', session, facilityId),
    enabled: Boolean(ADMIN_API_BASE_URL),
  })
  const drafts = useQuery({
    queryKey: ['partner-simulator', 'drafts', session.clientId, facilityId],
    queryFn: () => listDrafts(ADMIN_API_BASE_URL ?? '', session, facilityId),
    enabled: Boolean(ADMIN_API_BASE_URL),
  })
  const nameOf = (workerId: string): string => workers.find((worker) => worker.workerId === workerId)?.name ?? workerId
  const draftOf = (workerId: string) => drafts.data?.drafts.find((entry) => entry.workerId === workerId) ?? null
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-ink-mid">Every worker's shift preferences for {query.data ? describePeriod(query.data.period) : 'next month'}, as a scheduler reads them before building the schedule: shift order, hours, PTO asked for, days off by priority, and the draft each built from them <EndpointHint>GET /facilities/{'{facilityId}'}/schedule/drafts</EndpointHint>. A partner reads; the worker sets them.</p>
        <Button icon={RefreshCw} variant="secondary" size="sm" disabled={query.isFetching || drafts.isFetching} onClick={() => void Promise.all([query.refetch(), drafts.refetch()])}>
          Refresh
        </Button>
      </div>
      {query.isPending ? (
        <Skeleton />
      ) : query.isError ? (
        <ErrorNote message={describeError(query.error, 'The preferences could not be read.')} onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : query.data.preferences.length === 0 ? (
        <p className="text-xs text-ink-mid">Nobody has submitted preferences for {describePeriod(query.data.period)} yet.</p>
      ) : (
        <ul className="divide-y divide-marine/10 rounded-lg border border-marine/15 bg-white">
          {query.data.preferences.map((entry) => (
            <li key={entry.workerId} className="px-4 py-3">
              <div className="mb-1 text-sm font-bold text-ink">{nameOf(entry.workerId)}</div>
              <ShiftPreferencesSummary preferences={entry} period={entry.period} draft={draftOf(entry.workerId)} isStale={draftOf(entry.workerId) !== null && draftOf(entry.workerId)?.preferencesVersion !== entry.version} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
